import { useEffect, useState } from "react";

import { MatrixRain } from "@/components/MatrixRain";
import { Nav } from "@/components/Nav";
import MerchWheel from "@/components/MerchWheel";
import { FlashMessage } from "@/components/FlashMessage";
import RegionSelector from "@/components/RegionSelector";


export default function ComikReleaf({ setCart }) {
  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const [region, setRegion] = useState(() => {
    return localStorage.getItem("comikReleafRegion") || null;
  });
  const [activeCategory, setActiveCategory] = useState("all");
  const [merchProducts, setMerchProducts] = useState([]);
  const [merchLoading, setMerchLoading] = useState(true);
  const [merchError, setMerchError] = useState(null);
  const handleRegionSelect = (selectedRegion) => {
    localStorage.setItem(
      "comikReleafRegion",
      selectedRegion
    );

    setRegion(selectedRegion);
  };  

  useEffect(() => {
    const controller = new AbortController();

    const loadMerchProducts = async () => {
      try {
        setMerchLoading(true);
        setMerchError(null);

        const catalogBaseUrl =
          "https://assets.comikreleaf.com/storefront";

        // Fetch the latest published catalog revision.
        const manifestResponse = await fetch(
          `${catalogBaseUrl}/manifest.json`,
          {
            signal: controller.signal,
            cache: "no-cache",
          }
        );

        if (!manifestResponse.ok) {
          throw new Error(
            `Catalog manifest returned HTTP ${manifestResponse.status}`
          );
        }

        const manifest = await manifestResponse.json();

        if (
          !Number.isSafeInteger(manifest.revision) ||
          manifest.revision < 0
        ) {
          throw new Error("Invalid catalog manifest revision");
        }

        // Construct the catalog URL ourselves rather than
        // trusting an arbitrary URL supplied by the manifest.
        const catalogUrl =
          `${catalogBaseUrl}/revisions/${manifest.revision}.json`;

        const catalogResponse = await fetch(
          catalogUrl,
          {
            signal: controller.signal,
          }
        );

        if (!catalogResponse.ok) {
          throw new Error(
            `Catalog returned HTTP ${catalogResponse.status}`
          );
        }

        const data = await catalogResponse.json();

        if (!Array.isArray(data)) {
          throw new Error("Invalid storefront catalog");
        }

        if (!controller.signal.aborted) {
          setMerchProducts(data);
        }
      } catch (error) {
        if (error.name === "AbortError") {
          return;
        }

        console.error("Failed to load merch catalog:", error);

        if (!controller.signal.aborted) {
          setMerchError(
            "Merch catalog unavailable. Please try again later."
          );
        }
      } finally {
        if (!controller.signal.aborted) {
          setMerchLoading(false);
        }
      }
    };

    loadMerchProducts();

    return () => {
      controller.abort();
    };
  }, []);


  const categories = [
    {
      id: "all",
      label: "All",
    },
    {
      id: "hats",
      label: "Hats",
    },
    {
      id: "t-shirts",
      label: "T-Shirts",
    },
    {
      id: "hoodies",
      label: "Hoodies",
    },
  ];



  const filteredProducts =
    activeCategory === "all"
      ? merchProducts
      : merchProducts.filter(
          (product) =>
            product.category?.toLowerCase() ===
            activeCategory.toLowerCase()
        );


  return (
    <div
      id="top"
      data-testid="comik-releaf-page"
      className="relative min-h-screen overflow-hidden bg-black grain scanlines"
    >
      {!region && (
        <RegionSelector
          onSelect={handleRegionSelect}
        />
      )}

              {/* Phoenix background video */}
      <video
        src="/assets/Matrix_phoenix_bird.mp4"
        autoPlay
        loop
        muted
        playsInline
        className="pointer-events-none absolute inset-0 z-0 h-full w-full object-cover"
        aria-hidden="true"
      />

      {/* Falling Matrix 0s and 1s */}
      <MatrixRain />

      {/* Radial vignette */}
      <div
        className="pointer-events-none fixed inset-0 z-[1]"
        style={{
          background:
            "radial-gradient(120% 90% at 50% 0%, rgba(0,0,0,0.2), rgba(0,0,0,0.88) 75%)",
        }}
        aria-hidden
      />

      <Nav />

      <FlashMessage />

      <main className="relative z-10">
        {/* Hero */}
        <section className="mx-auto max-w-6xl px-6 pb-16 pt-32">
          <div className="max-w-4xl">
            <div
              className="
                font-mono
                text-xs
                uppercase
                tracking-[0.3em]
                text-matrix-green
              "
            >
              DORIANTE // MERCH
            </div>

            <h1
              className="
                mt-4
                font-display
                text-5xl
                font-black
                uppercase
                leading-none
                tracking-tight
                text-white
                sm:text-6xl
                md:text-7xl
              "
            >
              COMIK
              <span className="text-matrix-green"> RELEAF</span>
            </h1>

            <div
              className="
                mt-5
                h-px
                w-24
                bg-matrix-green
                shadow-[0_0_12px_rgba(0,255,100,0.5)]
              "
            />

            <p
              className="
                mt-6
                max-w-2xl
                font-mono
                text-sm
                leading-relaxed
                text-white/60
                sm:text-base
              "
            >
              Wear the chaos. Comik Releaf is the merch side of the
              Doriantelive universe — original designs, questionable
              decisions, and apparel for people who don't take life
              too seriously.
            </p>
          </div>
        </section>

        {/* Category navigation */}
        <section className="mx-auto max-w-6xl px-6">
          <div
            className="
              border-y
              border-matrix-green/20
              py-4
            "
          >
            <div
              className="
                flex
                flex-wrap
                items-center
                gap-3
              "
            >
              <span
                className="
                  mr-2
                  font-mono
                  text-[10px]
                  uppercase
                  tracking-[0.2em]
                  text-white/30
                "
              >
                Browse //
              </span>

              {categories.map((category) => (
                <button
                  key={category.id}
                  type="button"
                  onClick={() => setActiveCategory(category.id)}
                  className={`
                    border
                    px-5
                    py-2
                    font-mono
                    text-[10px]
                    uppercase
                    tracking-[0.18em]
                    transition-all
                    duration-200
                    ${
                      activeCategory === category.id
                        ? `
                          border-matrix-green
                          bg-matrix-green/10
                          text-matrix-green
                          shadow-[0_0_15px_rgba(0,255,100,0.08)]
                        `
                        : `
                          border-white/10
                          text-white/50
                          hover:border-matrix-green/40
                          hover:text-matrix-green
                        `
                    }
                  `}
                >
                  {category.label}
                </button>
              ))}
            </div>
          </div>
        </section>

        {/* Product catalogue */}
        <section className="mx-auto max-w-6xl px-6 py-12">
          <div className="mb-8 flex items-end justify-between gap-4">
            <div>
              <div
                className="
                  font-mono
                  text-[10px]
                  uppercase
                  tracking-[0.25em]
                  text-matrix-green/70
                "
              >
                Comik Releaf // Catalogue
              </div>

              <h2
                className="
                  mt-2
                  font-display
                  text-2xl
                  font-bold
                  uppercase
                  tracking-wide
                  text-white
                  sm:text-3xl
                "
              >
                {activeCategory === "all"
                  ? "All Products"
                  : categories.find(
                      (category) => category.id === activeCategory
                    )?.label}
              </h2>
            </div>

            {filteredProducts.length > 0 && (
              <div
                className="
                  font-mono
                  text-[9px]
                  uppercase
                  tracking-widest
                  text-white/30
                "
              >
                {filteredProducts.length} Items
              </div>
            )}
          </div>

          {merchLoading ? (
              <div className="py-16 text-center">
                <div className="font-mono text-xs uppercase tracking-[0.2em] text-matrix-green/70">
                  Loading // Comik Releaf Catalogue
                </div>
              </div>
            ) : merchError ? (
              <div className="py-16 text-center">
                <div className="font-mono text-xs uppercase tracking-[0.2em] text-red-400">
                  {merchError}
                </div>
              </div>
            ) : (

            <MerchWheel
              products={filteredProducts}
              setCart={setCart}
              region={region}
              backendUrl={backendUrl}
            />

            )}
        </section>


        {/* Brand footer statement */}
        <section className="mx-auto max-w-6xl px-6 pb-24 pt-12">
          <div
            className="
              border
              border-matrix-green/10
              bg-matrix-green/[0.02]
              p-8
              text-center
            "
          >
            <div
              className="
                font-mono
                text-[9px]
                uppercase
                tracking-[0.3em]
                text-matrix-green/50
              "
            >
              COMIK RELEAF // DORIANTE LIVE
            </div>

            <p
              className="
                mx-auto
                mt-4
                max-w-xl
                font-display
                text-xl
                uppercase
                tracking-wide
                text-white/70
              "
            >
              Because apparently the internet needed more stupid shirts.
            </p>
          </div>
        </section>
      </main>
    </div>
  );
}
